'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useParams, useSearchParams } from 'next/navigation';
import Navbar from '../../../../../components/Navbar';
import Footer from '../../../../../components/Footer';
import InterestedPopup from '../../../../../components/InterestedPopup';
import PropertyGallery from '../../../../../components/PropertyGallery';
import { api } from '@/lib/apiClient';
import Cookies from 'js-cookie';
const BASE_URL = process.env.NEXT_PUBLIC_API_URL || '/api/proxy/';

// Mock Project Data
const mockProjectDetails = {
    'proj-001': {
        id: 'proj-001',
        title: 'Prestige Cyber Towers',
        builder: 'Prestige Group',
        reraId: 'TN/01/Building/0123/2024',
        status: 'Under Construction',
        possession: 'Dec 2025',
        total_units: 450,
        project_area_acres: 5,
        priceRange: '₹85 Lac - ₹2.5 Cr',
        location: {
            area: 'Sholinganallur',
            city: 'Chennai',
            address: 'OMR, Sholinganallur, Chennai - 600119'
        },
        configurations: ['2 BHK', '3 BHK', '4 BHK'],
        amenities: ['Clubhouse', 'Swimming Pool', 'Gym', 'Mini Theatre', 'Tennis Court', 'Children Play Area', 'Landscaped Gardens', '24/7 Security'],
        description: 'Prestige Cyber Towers is a premium residential project offering luxury apartments in the heart of OMR. Designed for the modern professional, it combines connectivity with tranquility.',
        images: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1200',
    }
};
const property = {
    agent: {
        name: "John Doe",
        role: "Property Agent"
    },
    costs: {
        rent: 25000,
        deposit: 300000
    }
};

function convertToArray(str) {
    if (!str) return [];
    return str.split('-').map(num => parseInt(num, 10));
}

function formatPrice(value) {
    if (value == null || value === '') return null;
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    if (num >= 100) {
        const cr = num / 100;
        return `₹${parseFloat(cr.toFixed(2))} Cr`;
    }
    return `₹${parseFloat(num.toFixed(2))} Lac`;
}

function cleanNumber(value) {
    if (value == null || value === '') return null;
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return parseFloat(num.toFixed(2));
}

function formatPriceRange(minPrice, maxPrice, fallback) {
    const min = formatPrice(minPrice);
    const max = formatPrice(maxPrice);
    if (min && max) return `${min} - ${max}`;
    if (min) return `${min} onwards`;
    if (fallback) {
        // Clean trailing zeros from any numbers in the fallback string
        return fallback.replace(/(\d+\.\d+)/g, (match) => parseFloat(parseFloat(match).toFixed(2)));
    }
    return 'Price on request';
}

// Mock Ad Data 
export default function ProjectDetailPage() {
    const params = useParams();
    const projectId = params.id;
    // const project = mockProjectDetails[projectId] || mockProjectDetails['proj-001'];
    const [project, setProject] = useState({});
    const [activeImage, setActiveImage] = useState(0);
    const [showInterestedPopup, setShowInterestedPopup] = useState(false);
    const [isLoggedInContext, setIsLoggedInContext] = useState(false);
    const searchParams = useSearchParams();
    const cityId = searchParams.get("cityId");
    const [localities, setLocalities] = useState("");
    const [loading, setLoading] = useState(true);
    const [searchData, setSearchData] = useState("");
    const [userId, setUserId] = useState("");

    useEffect(() => {
        fetchProperty();
    }, [projectId]);

    const fetchProperty = async () => {
        const localitiesArray = convertToArray(searchParams.get("localities"));
        setLocalities(localitiesArray);
        try {
            const res = await api.post('selectwithout/view_home_project_details', {
                sqlData: "*",
                where: "id=$1",
                whereData: [projectId],
            });
            console.log("res.data", res.data);
            if (res.success === 1 && Array.isArray(res.data) && res.data.length > 0) {
                const fetchedProject = res.data[0];
                setProject(fetchedProject);
                const sData = "title=" + (fetchedProject?.title || '') + "&city_id=" + cityId + "&listing_type=1" + "&localities=" + localitiesArray.join(",") + "&id=" + projectId;
                setSearchData(sData);
                const currentViews = fetchedProject?.view_count || 0;
                await api.post('updatewithout/listings/' + projectId, { views_count: currentViews + 1 }, { skipAuthGuard: true });
                const user = Cookies.get('id');
                if (user) {
                    setUserId(user.replace(/^"|"$/g, ''));
                    setIsLoggedInContext(true);
                    console.log("user", user);
                }
            } else {
                setProject(null);
            }
        } catch (error) {
            console.error('Failed to fetch project:', error);
            setProject(null);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <>
                <Navbar />
                <main className="min-h-screen bg-slate-50 font-sans pb-16 flex items-center justify-center">
                    <div className="text-center">
                        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-gray-500">Loading project details...</p>
                    </div>
                </main>
                <Footer />
            </>
        );
    }

    if (!project || Object.keys(project).length === 0) {
        return (
            <>
                <Navbar />
                <main className="min-h-screen bg-slate-50 font-sans pb-16 flex items-center justify-center">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Project Not Found</h2>
                        <p className="text-gray-500 mb-4">The project you are looking for does not exist or has been removed.</p>
                        <Link href="/" className="btn btn-primary">Return Home</Link>
                    </div>
                </main>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Navbar />
            <main className="min-h-screen bg-slate-50 font-sans pb-16">

                {/* Hero Section */}
                <div className="relative h-[60vh] md:h-[70vh] bg-gray-900 text-white">
                    <img
                        src={project?.images ? (project.images.startsWith('http') ? project.images : `${BASE_URL}${project.images}`) : '/no-image.jpeg'}
                        className="w-full h-full object-cover"
                        alt=""
                    />
                    {/* <PropertyGallery
                        images={project?.images}
                        baseUrl={BASE_URL}
                        title={project?.title}
                    /> */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent flex flex-col justify-end p-6 md:p-12">
                        <div className="container-custom">
                            <div className="max-w-4xl">
                                <div className="flex flex-wrap gap-2 text-sm font-semibold mb-4">
                                    <span className="px-3 py-1 bg-primary rounded-full">{project.status}</span>
                                    <span className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full border border-white/30">RERA: {project.reraId}</span>
                                </div>
                                <h1 className="text-4xl md:text-6xl font-bold mb-4">{project.title}</h1>
                                <p className="text-xl md:text-2xl text-gray-200 mb-6">By {project?.builder} • {project?.location?.area}</p>
                                <div className="flex flex-wrap gap-6 text-lg">
                                    <div>
                                        <p className="text-gray-400 text-sm uppercase tracking-wider">Price Range</p>
                                        <p className="font-bold">{formatPriceRange(project.min_price, project.max_price, project.priceRange)}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-400 text-sm uppercase tracking-wider">Possession</p>
                                        <p className="font-bold">{project.possession}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-400 text-sm uppercase tracking-wider">Configurations</p>
                                        <p className="font-bold">{project?.configurations?.join(', ')}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="container-custom mt-12">
                    <div className="grid lg:grid-cols-3 gap-12 items-start">

                        {/* Main Info */}
                        <div className="lg:col-span-2 space-y-6 mt-4">
                            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Project Overview</h2>
                                <p className="text-gray-600 leading-relaxed text-lg">{project.description}</p>

                                <div className="grid md:grid-cols-3 gap-6 mt-8 p-6 bg-gray-50 rounded-xl">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-gray-900 mb-1">{project.total_units || 'N/A'}</div>
                                        <div className="text-sm text-gray-500 uppercase">Total Units</div>
                                    </div>
                                    <div className="text-center border-l border-r border-gray-200">
                                        <div className="text-2xl font-bold text-gray-900 mb-1">{project.project_area_acres ? cleanNumber(project.project_area_acres) : 'N/A'}</div>
                                        <div className="text-sm text-gray-500 uppercase">Project Area (Acres)</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-gray-900 mb-1">{project.total_towers || 'N/A'}</div>
                                        <div className="text-sm text-gray-500 uppercase">Total Towers</div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                                <h2 className="text-2xl font-bold text-gray-900 mb-8">Project Details</h2>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-y-8 gap-x-6">
                                    <div>
                                        <p className="text-sm text-gray-500 uppercase mb-1">Project Type</p>
                                        <p className="font-semibold text-gray-900 capitalize">{project.project_type || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 uppercase mb-1">Status</p>
                                        <p className="font-semibold text-gray-900 capitalize">{project.status || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 uppercase mb-1">RERA ID</p>
                                        <p className="font-semibold text-gray-900">{project.rera_id || project.reraId || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 uppercase mb-1">Launch Date</p>
                                        <p className="font-semibold text-gray-900">
                                            {project.launch_date ? new Date(project.launch_date).toLocaleDateString() : 'N/A'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 uppercase mb-1">Completion Date</p>
                                        <p className="font-semibold text-gray-900">
                                            {project.completion_date ? new Date(project.completion_date).toLocaleDateString() : 'N/A'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 uppercase mb-1">Price Range</p>
                                        <p className="font-semibold text-gray-900">
                                            {formatPriceRange(project.min_price, project.max_price, project.priceRange)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Sidebar */}
                        <aside className="lg:col-span-1 space-y-6 lg:sticky lg:top-24 mt-4">
                            {/* Rental Summary Card */}
                            <div className="bg-white rounded-2xl shadow-lg border border-primary/20 p-6 relative overflow-hidden">
                                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-purple-600"></div>

                                <h4 className="font-bold text-gray-900 mb-4 text-sm uppercase">Listed by</h4>
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="w-10 h-10 bg-gradient-to-br from-gray-700 to-gray-900 rounded-full flex items-center justify-center text-white text-lg font-bold shadow-md">
                                        {property.agent.name.charAt(0)}
                                    </div>
                                    <div>
                                        <h5 className="font-bold text-gray-900">{property.agent.name}</h5>
                                        <p className="text-xs text-gray-500">{property.agent.role}</p>
                                    </div>
                                </div>
                                <div className="flex justify-between items-end mb-6">
                                    <div>
                                        <p className="text-gray-500 text-sm font-medium uppercase">Monthly Rent</p>
                                        <h3 className="text-2xl font-bold text-gray-900">₹{property.costs.rent != null ? property.costs.rent.toLocaleString() : 'N/A'}</h3>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-sm text-gray-500 block">Deposit</span>
                                        <span className="font-semibold text-gray-800">{property.costs.deposit != null ? `₹${(property.costs.deposit / 100000).toFixed(1)} Lac` : 'N/A'}</span>
                                    </div>
                                </div>

                                <button onClick={() => { setShowInterestedPopup(true); }} className="w-full btn btn-primary py-3.5 text-base font-bold shadow-primary/30 shadow-md mb-4">
                                    I'm Interested
                                </button>

                                <div className="pt-4 border-t border-gray-100 flex items-center justify-center gap-2 text-xs text-gray-400">
                                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                                    Your details are safe with us
                                </div>
                            </div>
                        </aside>

                    </div>
                </div>
            </main>
            <InterestedPopup
                isOpen={showInterestedPopup}
                onClose={() => setShowInterestedPopup(false)}
                loggedInPerson={isLoggedInContext}
                listingId={projectId}
                mode="project"
                phone={project.agent?.phone}
                agentName={project.agent?.name}
                searchData={searchData}
            />
            <Footer />
        </>
    );
}
